import * as THREE from 'three';
/* ============================================================
   特效貼圖：全部程序生成（canvas），零外部檔案
   ============================================================

   **風格：實彈／軍械**（使用者 2026-08-05 第三次定調）。
   前兩版走過「動漫機甲 VFX」與「極簡霓虹幾何」，都被否掉，
   原話是「**泛光太重，看起來很像魔法攻擊；我要的是機甲飛彈那種打擊感**」。

   ## 「魔法感」的三個來源（這一版全部拿掉）

   1. **加法混色的大面積光**：任何超過泛光門檻 0.66 的東西都會被糊成光暈，
      而光暈就是魔法。這一版只有「真的在燒的那一瞬間」可以過門檻，
      而且面積要小、時間要短（< 0.1 秒）。
   2. **擴散的幾何環**：六角框、同心圓從中心擴出去 —— 那是法陣的語言。
      實彈的地面回饋是**揚塵**：不規則、有厚度、會被風帶走，而且是**暗的**。
   3. **冷色的能量**：青、藍、紫。實彈的色譜只有三段 ——
      白熱（瞬間）→ 橙（火）→ 灰褐（煙與塵）。

   ## 所以這一版的貼圖分兩類

   - **加法**（會發光）：火光、火星、彈道。全部又小又短。
   - **一般混色**（不發光）：煙、塵、碎片。它們**遮住背後的東西**，
     那正是「有實體」的感覺來源 —— 光只會疊加，煙會擋住。

   ## encoding

   three r128 沒有 ColorManagement，`outputEncoding = sRGBEncoding`
   會把 linear 貼圖提亮近兩倍（2026-08-04 教訓）。標成 sRGB 之後
   畫布上調好的濃淡就是螢幕上的濃淡 —— 煙的層次全靠這個。

   ## headless

   Node 裡沒有 `document`，`src/sim` 會 import 到這條鏈。取不到畫布回 `null`，
   由 `vfx-pool.ts` 的 `READY` 統一擋掉所有生成呼叫。 */

var HAS_DOC = typeof document !== 'undefined' && typeof document.createElement === 'function';
var cache: Record<string, THREE.Texture | null> = {};

function make(key: string, w: number, h: number, draw: (d: Uint8ClampedArray, w: number, h: number) => void) {
  if (key in cache) return cache[key];
  if (!HAS_DOC) { cache[key] = null; return null; }
  var c = document.createElement('canvas'); c.width = w; c.height = h;
  var g = c.getContext('2d'); if (!g) { cache[key] = null; return null; }
  var img = g.createImageData(w, h);
  draw(img.data, w, h);
  g.putImageData(img, 0, 0);
  var t = new THREE.CanvasTexture(c);
  (t as any).encoding = (THREE as any).sRGBEncoding;
  t.needsUpdate = true;
  cache[key] = t;
  return t;
}

function put(d: Uint8ClampedArray, i: number, a: number) {
  d[i] = d[i + 1] = d[i + 2] = 255;
  d[i + 3] = (Math.max(0, Math.min(1, a)) * 255) | 0;
}

/** 固定的偽隨機（同一組輸入永遠同一個值）。貼圖只生成一次，
    所以這裡不能用 Math.random —— 否則每次開遊戲的煙長得不一樣。 */
function hash(n: number) {
  var s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

/* ---------- 加法類：火光與彈道 ---------- */

/** 槍口火焰：**不對稱的星芒**。
    對稱的星星讀成魔法，不對稱、長度不一的才讀成「一團被推出去的火」。
    六道芒的長度用固定亂數決定，中間是很小的白熱核心。 */
export function texFlash() {
  return make('flash', 128, 128, function (d, w) {
    var r = w / 2;
    var LEN = [1.0, 0.42, 0.74, 0.30, 0.86, 0.52];
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var th = Math.atan2(dy, dx);
      // 核心：小而急促
      var a = Math.pow(Math.max(0, 1 - rad / 0.26), 2.6);
      // 芒：依角度取最近的兩道做內插，讓長度沿角度連續變化
      var seg = 6, f = (th + Math.PI) / (Math.PI * 2) * seg;
      var i0 = Math.floor(f) % seg, i1 = (i0 + 1) % seg, tt = f - Math.floor(f);
      var L = LEN[i0] * (1 - tt) + LEN[i1] * tt;
      var spike = Math.pow(Math.max(0, 1 - rad / (0.34 + L * 0.62)), 3.4);
      put(d, (y * w + x) * 4, Math.min(1, a + spike * 0.72));
    }
  });
}

/** 火星／曳光：頭端銳、尾端收成一點。**細**是重點 ——
    粗的曳光讀成雷射，細的才讀成金屬碎屑在飛。 */
export function texSpark() {
  return make('spark', 256, 32, function (d, w, h) {
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
      var u = x / (w - 1), v = (y + 0.5) / h * 2 - 1;
      var head = u < 0.06 ? 1 : Math.pow(1 - (u - 0.06) / 0.94, 1.8);
      var prof = Math.pow(Math.max(0, 1 - Math.abs(v) / Math.max(0.05, head * 0.55)), 2.2);
      put(d, (y * w + x) * 4, prof * head);
    }
  });
}

/** 火球核心：柔邊圓，只給爆炸最前面那 0.1 秒。 */
export function texFire() {
  return make('fire', 96, 96, function (d, w) {
    var r = w / 2;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      // 邊緣加一點不規則，火球不該是完美的圓
      var wob = 1 + 0.13 * Math.sin(Math.atan2(dy, dx) * 5.0);
      put(d, (y * w + x) * 4, Math.pow(Math.max(0, 1 - rad / (0.9 * wob)), 1.9));
    }
  });
}

/* ---------- 一般混色類：煙、塵、碎片 ---------- */

/** 煙：柔邊 + 五團偏心的濃淡。用**一般混色**畫，所以它會遮住背後的東西 ——
    那是「這團東西有實體」唯一的線索，加法混色永遠做不到。 */
export function texSmoke() {
  return make('smoke', 128, 128, function (d, w) {
    var r = w / 2;
    var LOBE: number[][] = [];
    for (var k = 0; k < 5; k++) {
      LOBE.push([hash(k + 1) * 0.9 - 0.45, hash(k + 9) * 0.9 - 0.45, 0.38 + hash(k + 17) * 0.30]);
    }
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var base = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy) / 0.95);
      var lump = 0;
      for (var i = 0; i < LOBE.length; i++) {
        var L = LOBE[i], ex = dx - L[0], ey = dy - L[1];
        lump = Math.max(lump, Math.max(0, 1 - Math.sqrt(ex * ex + ey * ey) / L[2]));
      }
      put(d, (y * w + x) * 4, Math.pow(base, 1.35) * (0.30 + 0.70 * lump));
    }
  });
}

/** 揚塵：比煙更扁更淡，給地面用。**取代上一版的擴散幾何環** ——
    爆炸在地上留下的是一圈被掀起來的灰，不是一個發光的圓。
    做成環狀但邊界模糊，而且內圈不是空的（有殘留的薄塵）。 */
export function texDust() {
  return make('dust', 256, 256, function (d, w) {
    var r = w / 2;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      if (rad >= 1) { put(d, (y * w + x) * 4, 0); continue; }
      var th = Math.atan2(dy, dx);
      // 沿角度起伏：塵環不是等厚的
      var wob = 0.80 + 0.20 * Math.sin(th * 3.0 + 0.7) * Math.sin(th * 7.0);
      var band = Math.exp(-Math.pow((rad - 0.78 * wob) / 0.22, 2));   // 外圈那一道
      var inner = Math.max(0, 1 - rad / 0.9) * 0.22;                  // 內側的薄塵
      put(d, (y * w + x) * 4, Math.min(1, band * 0.85 + inner));
    }
  });
}

/** 碎片：不規則的實心塊。旋轉與縮放由呼叫端給。 */
export function texChunk() {
  return make('chunk', 64, 64, function (d, w) {
    var r = w / 2;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var th = Math.atan2(dy, dx);
      // 五邊形被壓歪：邊界半徑沿角度不規則
      var e = 0.62 + 0.26 * Math.sin(th * 2.0 + 1.1) + 0.12 * Math.sin(th * 5.0);
      put(d, (y * w + x) * 4, rad < e ? 1 : 0);
    }
  });
}

/* ---------- 標記類：不是特效，是資訊 ---------- */

/** 細圓環（線稿）。**只給「範圍／落點標記」用**，不給爆點用。

    為什麼是圓不是六角：六角框從中心擴出去是法陣的語言（上一版就死在這裡）。
    圓形細環是**瞄準鏡的語言** —— 它不會動、不會擴散，只是靜靜標出
    「這一圈之內會被打到」。玩家讀它是資訊而不是特效。 */
export function texRing(th: number) {
  return make('ring' + th, 256, 256, function (d, w) {
    var r = w / 2, aa = 1.4 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var a = 1;
      if (rad > 0.97) a = Math.max(0, 1 - (rad - 0.97) / aa);
      else if (rad < 0.97 - th) a = Math.max(0, 1 - (0.97 - th - rad) / aa);
      put(d, (y * w + x) * 4, a);
    }
  });
}

/** 正 n 邊形在角度 θ 上的半徑（外接半徑 = 1）。 */
function polyR(n: number, th: number) {
  var seg = Math.PI * 2 / n;
  var a = th - Math.floor(th / seg) * seg - seg / 2;
  return Math.cos(Math.PI / n) / Math.cos(a);
}

/** 六邊形線框。**跟 `texRing` 一樣是「標記」不是「特效」** ——
    它標出「這一塊等一下會被打到」，不從中心擴散出去。

    為什麼軌道光柱用六角不用圓（使用者 2026-08-05 指定）：
    圓形的落點標記跟敵人腳下的接地影、精英環撞在一起分不出來；
    六角是**人造物的形狀**，一眼就讀成「這是武器系統標定的區域」。 */
export function texPolyRing(n: number, th: number) {
  return make('pring' + n + '_' + th, 256, 256, function (d, w) {
    var r = w / 2, aa = 1.5 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var rp = polyR(n, Math.atan2(dy, dx)) * 0.96;
      var a = 1;
      if (rad > rp) a = Math.max(0, 1 - (rad - rp) / aa);
      else if (rad < rp - th) a = Math.max(0, 1 - (rp - th - rad) / aa);
      put(d, (y * w + x) * 4, a);
    }
  });
}
